// src/hooks/usePatientData.js
import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../api/ApiService';
import { storeUserSession } from '../auth/utils/storage';

export const usePatientData = () => {
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  console.log('usePatientData initialized',patientData);

  // Function to get stored login data
  const getStoredData = useCallback(() => {
    try {
      const stored = localStorage.getItem('loginObj');
      const parsed = stored ? JSON.parse(stored) : {};
      console.log('🔍 Retrieved stored data:', parsed);
      return parsed;
    } catch (err) {
      console.error('Error parsing stored login data:', err);
      return {};
    }
  }, []);

  // Function to get ID from URL query parameters
  const getIdFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    console.log('🔍 ID from URL:', id);
    return id;
  }, []);

  // Function to fetch patient data
  const fetchPatientData = useCallback(async (id, forceUseStored = false) => {
    console.log('🚀 fetchPatientData called with ID:', id, 'forceUseStored:', forceUseStored);
    
    if (!id && !forceUseStored) {
      console.log('❌ No ID provided and not forcing stored data, skipping API call');
      setPatientData(null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get fresh stored data
      const storedData = getStoredData();
      console.log('📦 Current stored data:', storedData);
      
      // If forcing stored data or no ID, use stored data
      if (forceUseStored || !id) {
        if (storedData && Object.keys(storedData).length > 0) {
          console.log('📝 Using stored data (forced or no ID)');
          setPatientData(storedData);
          sessionStorage.setItem("patientData", JSON.stringify(storedData));
          return;
        } else {
          console.log('⚠️ No stored data available');
          setPatientData(null);
          return;
        }
      }
      
      console.log('📡 Making API call to aes-sessions/' + id);
      
      // Call API: aes-sessions/{id}
      const result = await apiService.fetchPatientById(id);
      console.log('✅ API response:', result);
      
      // Use result if it exists and has data, otherwise fall back to stored data
      let finalPatientData;
      
      if (result && Object.keys(result).length > 0) {
        // ✅ Remap fields and auto-login
        const remappedData = {
          patientCode: result.patientId,
          sessionId: result.sessionId,
          firstName: result.firstname,
          lastName: result.lastname,
          mobileNumber: result.mobile,
          authToken: result.authToken || '',
          email: result.email || '',
          salutation: result.salutation || '',
          dob: result.dob || '',
          gender: result.gender || '',
          sourcechannel: result.sourcechannel || '',  
          createdBy: result.createdBy || '',
        };

        storeUserSession(remappedData); // ✅ AUTO-LOGIN HAPPENS HERE
        console.log('✅ Auto-login via session ID complete');

        finalPatientData = result; 
        console.log('📝 Using API result as patient data');
      } else if (storedData && Object.keys(storedData).length > 0) {
        finalPatientData = storedData;
        console.log('📝 Using stored data as fallback');
      } else {
        finalPatientData = null;
        console.log('⚠️ No valid data available');
      }
      
      setPatientData(finalPatientData);
      
      // Store in sessionStorage for later use
      if (finalPatientData) {
        sessionStorage.setItem("patientData", JSON.stringify(finalPatientData));
      }
      
    } catch (err) {
      console.error('❌ API Error:', err);
      
      // On API error, try to use stored data as fallback
      const storedData = getStoredData();
      if (storedData && Object.keys(storedData).length > 0) {
        console.log('📝 Using stored data due to API error');
        setPatientData(storedData);
        sessionStorage.setItem("patientData", JSON.stringify(storedData));
      } else {
        setPatientData(null);
      }
      
      let errorMessage = 'Failed to fetch patient data';
      
      if (err.response) {
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = 'Network error: No response from server';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getStoredData]);

  // Auto-fetch when component mounts or URL changes
  const fetchCurrentPatient = useCallback(() => {
    console.log('🔄 fetchCurrentPatient called');
    const id = getIdFromUrl();
    fetchPatientData(id);
  }, [getIdFromUrl, fetchPatientData]);

  // Initial fetch on mount
  useEffect(() => {
    console.log('useEffect triggered - fetching current patient');
    fetchCurrentPatient();
  }, [fetchCurrentPatient]);

  // Listen for URL changes (for SPA navigation)
  useEffect(() => {
    const handleUrlChange = () => {
      console.log('🔄 URL changed - refetching patient');
      fetchCurrentPatient();
    };

    window.addEventListener('popstate', handleUrlChange);
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [fetchCurrentPatient]);

  // NEW: Listen for login completion
  useEffect(() => {
    const handleLoginComplete = (event) => {
      console.log('🔐 Login completed, checking for patient data');
      const loginData = event.detail?.loginData;
      
      if (loginData) {
        console.log('📝 Login data available, using as patient data');
        setPatientData(loginData);
        sessionStorage.setItem("patientData", JSON.stringify(loginData));
      } else {
        // Re-fetch with current URL ID or force use stored data
        const id = getIdFromUrl();
        if (id) {
          fetchPatientData(id);
        } else {
          fetchPatientData(null, true); // Force use stored data
        }
      }
    };

    window.addEventListener('loginComplete', handleLoginComplete);
    
    return () => {
      window.removeEventListener('loginComplete', handleLoginComplete);
    };
  }, [fetchPatientData, getIdFromUrl]);

  // NEW: Listen for localStorage changes (in case of multiple tabs)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'loginObj') {
        console.log('💾 localStorage loginObj changed');
        // Re-evaluate patient data
        const id = getIdFromUrl();
        if (!id) {
          fetchPatientData(null, true); // Force use new stored data
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchPatientData, getIdFromUrl]);

  // NEW: Listen for logout events
  useEffect(() => {
    const handleLogoutComplete = (event) => {
      console.log('🚪 Logout completed, clearing patient data');
      setPatientData(null);
      setError(null);
      // Clear sessionStorage as well
      sessionStorage.removeItem("patientData");
    };

    window.addEventListener('logoutComplete', handleLogoutComplete);
    
    return () => {
      window.removeEventListener('logoutComplete', handleLogoutComplete);
    };
  }, []);

  console.log('Current patientData state:', patientData);

  return {
    patientData,
    loading,
    error,
    refetch: fetchCurrentPatient,
    patientId: getIdFromUrl(),
    hasPatientId: !!getIdFromUrl(),
    // NEW: Method to manually set patient data (useful for login)
    setPatientData: (data) => {
      setPatientData(data);
      if (data) {
        sessionStorage.setItem("patientData", JSON.stringify(data));
      }
    }
  };
};